import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { wrapDekForNoteShares } from '@/features/notes/noteShareWraps';
import { sanitizeHtml } from '@/lib/sanitizeHtml';
import { formatDateTime } from '@/lib/format';
import { notesApi, type NoteVersionSummary } from '@/api/notes';
import {
  bytesToBase64,
  bytesToStr,
  decryptWithWrappedKey,
  encryptSym,
  generateDEK,
  strToBytes,
} from '@/crypto/sodium';
import { useAuthStore } from '@/stores/auth';
import { confirmDialog } from '@/stores/dialog';

export function VersionsSection({
  noteId,
  tenantId,
}: {
  noteId: string;
  tenantId: string;
}) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const keyPair = useAuthStore((s) => s.keyPair);
  const queryClient = useQueryClient();

  const list = useQuery({
    queryKey: ['note-versions', noteId],
    queryFn: () => notesApi.listVersions(noteId),
    enabled: expanded,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['note', noteId] });
    queryClient.invalidateQueries({ queryKey: ['note-versions', noteId] });
    queryClient.invalidateQueries({ queryKey: ['notes', tenantId] });
  };

  return (
    <section className="pt-4 mt-4 border-t border-[var(--border)]">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="flex items-center gap-2 text-xs uppercase text-[var(--text-muted)] font-semibold hover:text-[var(--text)] dark:hover:text-[var(--text)]"
      >
        <span>{expanded ? '▼' : '▶'}</span>
        {t('notes_ui.editor.versions_title')}
        {list.data && (
          <span className="text-[10px] normal-case text-[var(--text-dim)]">
            ({list.data.length})
          </span>
        )}
      </button>

      {expanded && (
        <div className="mt-3 space-y-2">
          {list.isLoading && (
            <p className="text-xs text-[var(--text-muted)]">{t('common.loading')}</p>
          )}
          {list.data && list.data.length === 0 && (
            <p className="text-xs text-[var(--text-muted)]">
              {t('notes_ui.editor.versions_empty')}
            </p>
          )}
          {list.data?.map((v) => (
            <NoteVersionCard
              key={v.id}
              noteId={noteId}
              tenantId={tenantId}
              version={v}
              keyPair={keyPair}
              onRestored={invalidate}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function NoteVersionCard({
  noteId,
  tenantId,
  version,
  keyPair,
  onRestored,
}: {
  noteId: string;
  tenantId: string;
  version: NoteVersionSummary;
  keyPair: { publicKey: Uint8Array; privateKey: Uint8Array } | null;
  onRestored: () => void;
}) {
  const [html, setHtml] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);
  const userId = useAuthStore((s) => s.user?.id);
  const { t } = useTranslation();

  const reveal = async () => {
    if (!keyPair) {
      setErr(t('notes_ui.editor.no_key'));
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      const detail = await notesApi.getVersion(noteId, version.version);
      const plainBytes = await decryptWithWrappedKey(detail, keyPair);
      // Conținut scris de oricine are drept de scriere pe notiță — sanitizat
      // înainte de `dangerouslySetInnerHTML` (vezi lib/sanitizeHtml.ts).
      setHtml(sanitizeHtml(bytesToStr(plainBytes)));
    } catch (e) {
      setErr(e instanceof Error ? e.message : t('notes_ui.editor.generic_error'));
    } finally {
      setBusy(false);
    }
  };

  const restore = async () => {
    if (!keyPair) return;
    if (
      !await confirmDialog(
        t('notes_ui.editor.restore_confirm', { version: version.version }),
      )
    ) {
      return;
    }
    setErr(null);
    setRestoring(true);
    try {
      // Decrypt plaintext vechi (reutilizează html dacă deja revealed)
      let oldHtml = html;
      if (!oldHtml) {
        const detail = await notesApi.getVersion(noteId, version.version);
        const plainBytes = await decryptWithWrappedKey(detail, keyPair);
        oldHtml = bytesToStr(plainBytes);
      }

      // Re-encrypt cu DEK nou (forward secrecy)
      const newDek = await generateDEK();
      const { ciphertext, nonce } = await encryptSym(strToBytes(oldHtml), newDek);
      // Wrap pentru TOȚI userii cu share — altfel restore-ul le taie accesul.
      const wraps = await wrapDekForNoteShares({
        noteId,
        tenantId,
        dek: newDek,
        keyPair,
        userId,
      }).finally(() => newDek.fill(0)); // DEK-ul nu mai e necesar după împachetare

      await notesApi.update(noteId, {
        title: version.title,
        parent_id: version.parent_id,
        ciphertext: bytesToBase64(ciphertext),
        nonce: bytesToBase64(nonce),
        ...wraps,
        change_reason: `restored from v${version.version}`,
      });
      onRestored();
    } catch (e) {
      setErr(e instanceof Error ? e.message : t('notes_ui.editor.restore_error'));
    } finally {
      setRestoring(false);
    }
  };

  return (
    <div className="border border-[var(--border-strong)] rounded-md px-3 py-2 text-sm">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-[var(--text)]">
              v{version.version}
            </span>
            <span className="text-xs text-[var(--text-muted)] truncate">
              {version.title}
            </span>
            <span className="text-xs text-[var(--text-dim)]">
              {formatDateTime(version.changed_at)}
            </span>
          </div>
          {version.change_reason && (
            <div className="text-xs text-[var(--text-muted)] mt-0.5 italic truncate">
              „{version.change_reason}"
            </div>
          )}
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          <button
            type="button"
            className="text-xs text-[var(--text-muted)] hover:text-brand-700 dark:hover:text-brand-300 disabled:opacity-50"
            onClick={restore}
            disabled={busy || restoring}
          >
            {restoring ? t('notes_ui.editor.restoring') : t('notes_ui.editor.restore')}
          </button>
          {html ? (
            <button
              type="button"
              className="text-xs text-[var(--text-muted)] hover:text-brand-700 dark:hover:text-brand-300"
              onClick={() => setHtml(null)}
            >
              {t('notes_ui.editor.hide')}
            </button>
          ) : (
            <button
              type="button"
              className="text-xs text-brand-700 dark:text-brand-300 hover:underline disabled:opacity-50"
              onClick={reveal}
              disabled={busy || restoring}
            >
              {busy ? t('notes_ui.editor.decrypting') : t('notes_ui.editor.reveal')}
            </button>
          )}
        </div>
      </div>

      {err && <div className="text-xs text-red-600 dark:text-red-400 mt-2">{err}</div>}

      {html && (
        <div
          className="prose prose-sm mt-3 pt-3 border-t border-[var(--border)] max-w-none"
          dangerouslySetInnerHTML={{ __html: html }}
        />
      )}
    </div>
  );
}
